import React from "react";
import { Check } from "lucide-react";

/**
 * Tre mockup stile chat Telegram che raccontano il flusso in 15 secondi:
 *  1. /menu → menu del giorno
 *  2. Tap su una porzione (inline buttons)
 *  3. Conferma + riepilogo preparatore
 *
 * Non sono screenshot reali — sono ricostruiti in HTML/CSS per coerenza grafica.
 */
export default function TelegramShowcase() {
  return (
    <section className="bg-[#17212B] text-white py-16 sm:py-24" data-testid="telegram-showcase">
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-10 sm:mb-14">
          <div className="text-xs uppercase tracking-[0.25em] text-[#7eaece] font-semibold mb-3">
            Come funziona in 15 secondi
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            Dal bot Telegram. Tre tap. Pranzo prenotato.
          </h2>
          <p className="text-white/60 max-w-xl mx-auto mt-4 text-sm sm:text-base">
            Nessuna app da scaricare, nessun account in più. Si usa quel che già si ha aperto in tasca.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 sm:gap-8" data-testid="telegram-showcase-grid">
          <Phone step="1" label="Apri il bot e scrivi /menu" testid="tg-step-1">
            <BotMsg>
              <Line bold>🍱 Menu di oggi · giovedì 15 maggio</Line>
              <Spacer />
              <Line>🍝 <b>Pasta al pomodoro</b></Line>
              <Line muted>€ 5,50 · 8/10 porzioni · chiude 11:30</Line>
              <Line muted>📍 stanza 142, secondo piano</Line>
              <Spacer />
              <Line>🥗 <b>Insalata di farro</b></Line>
              <Line muted>€ 6,00 · 4/6 porzioni · chiude 11:30</Line>
              <Line muted>📍 ingresso mensa</Line>
              <Spacer />
              <Line muted>Tocca per prenotare ↓</Line>
              <InlineBtns>
                <Btn>🍝 Pasta · 5,50€</Btn>
                <Btn>🥗 Farro · 6,00€</Btn>
              </InlineBtns>
            </BotMsg>
          </Phone>

          <Phone step="2" label="Tap sul piatto, scegli porzioni" testid="tg-step-2">
            <UserMsg>🍝 Pasta · 5,50€</UserMsg>
            <BotMsg>
              <Line bold>🍝 Pasta al pomodoro</Line>
              <Line muted>Quante porzioni vuoi prenotare?</Line>
              <InlineBtns>
                <Btn small>1 · 5,50€</Btn>
                <Btn small>2 · 11,00€</Btn>
                <Btn small>3 · 16,50€</Btn>
              </InlineBtns>
              <InlineBtns>
                <Btn small ghost>annulla</Btn>
              </InlineBtns>
            </BotMsg>
          </Phone>

          <Phone step="3" label="Confermato. Ritiri all'ora di pranzo." testid="tg-step-3">
            <UserMsg>2 · 11,00€</UserMsg>
            <BotMsg>
              <Line bold success>
                <Check className="inline w-4 h-4 mr-1 -mt-0.5" />
                Prenotato!
              </Line>
              <Spacer />
              <Line>🍝 <b>Pasta al pomodoro</b> × 2</Line>
              <Line>💶 Totale: <b>11,00€</b></Line>
              <Line>📍 Ritiro: <b>stanza 142</b></Line>
              <Spacer />
              <Line muted>Ti ricorderemo alle 11:00.</Line>
              <Line muted>Puoi annullare entro le 11:30.</Line>
            </BotMsg>
          </Phone>
        </div>
      </div>
    </section>
  );
}

// ---------- Atoms ----------
function Phone({ step, label, children, testid }) {
  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#7eaece] font-semibold mb-3">
        Step {step}
      </div>
      <div className="relative w-full max-w-[290px] mx-auto">
        {/* Phone frame */}
        <div className="bg-[#0E1621] rounded-[2rem] border border-white/10 shadow-2xl overflow-hidden">
          {/* Status bar */}
          <div className="h-7 bg-[#17212B] flex items-center justify-between px-5 text-[10px] text-white/60 border-b border-white/5">
            <span>9:41</span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-1.5 bg-white/60 rounded-sm" />
              <span className="w-1.5 h-1.5 bg-white/60 rounded-full" />
            </span>
          </div>
          {/* Bot header */}
          <div className="bg-[#17212B] px-3 py-2.5 flex items-center gap-2 border-b border-white/5">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2AABEE] to-[#229ED9] flex items-center justify-center text-base">
              🍱
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-medium leading-tight">Bot della tua sede</div>
              <div className="text-[10px] text-[#7eaece] leading-tight">bot · online</div>
            </div>
          </div>
          {/* Chat area */}
          <div className="bg-[#0E1621] px-3 py-3 space-y-1.5 min-h-[300px]">
            {children}
          </div>
          {/* Input bar */}
          <div className="bg-[#17212B] px-3 py-2 flex items-center gap-2 border-t border-white/5">
            <div className="flex-1 bg-[#242F3D] rounded-full px-3 py-1.5 text-[11px] text-white/40">
              Messaggio…
            </div>
            <div className="w-7 h-7 rounded-full bg-[#2AABEE] flex items-center justify-center text-white text-xs">▲</div>
          </div>
        </div>
      </div>
      <div className="mt-4 text-center text-sm text-white/70 max-w-[280px]">{label}</div>
    </div>
  );
}

function BotMsg({ children }) {
  return (
    <div className="flex">
      <div className="bg-[#182533] rounded-lg rounded-tl-sm px-3 py-2 max-w-[85%] text-[11.5px] leading-relaxed">
        {children}
      </div>
    </div>
  );
}
function UserMsg({ children }) {
  return (
    <div className="flex justify-end">
      <div className="bg-[#2B5278] rounded-lg rounded-tr-sm px-3 py-1.5 max-w-[85%] text-[11.5px]">
        {children}
      </div>
    </div>
  );
}
function Line({ children, bold, muted, success }) {
  const cls = [
    "leading-snug",
    bold ? "font-medium" : "",
    muted ? "text-white/55" : "",
    success ? "text-[#5EBA7D]" : "",
  ].join(" ");
  return <div className={cls}>{children}</div>;
}
function Spacer() { return <div className="h-1.5" />; }
function InlineBtns({ children }) {
  return <div className="flex gap-1 mt-1.5 flex-wrap">{children}</div>;
}
function Btn({ children, small, ghost }) {
  return (
    <div className={`flex-1 text-center rounded-md border ${ghost ? "border-white/15 text-white/55" : "border-[#2AABEE]/40 text-[#7eaece]"} ${small ? "py-1 text-[10.5px]" : "py-1.5 text-[11px]"} px-1.5 min-w-0`}>
      {children}
    </div>
  );
}
